'use client';

import { signIn } from "next-auth/react";
import { useSearchParams } from "next/navigation";
import { Suspense, useState } from "react";
import { APP_CONFIG } from "@/config/app";
import { useBranding } from "@/hooks/useBranding";
import { Button } from '@/components/ui/button';
import { safeCallbackUrl } from "@/lib/safe-redirect";

function LoginForm() {
    const searchParams = useSearchParams();
    const rawCallbackUrl = searchParams.get("callbackUrl");
    const errorParam = searchParams.get("error");
    
    // Detect if we're on the platform domain (no tenant subdomain)
    const baseDomain = process.env.NEXT_PUBLIC_BASE_DOMAIN || 'pms.thesquare.dev';
    const isPlatformDomain = typeof window !== 'undefined'
        ? window.location.hostname === baseDomain || window.location.hostname === 'localhost'
        : false;
    
    // Where to go after signing in. It comes from the query string, so it is read when the page navigates and only an
    // absolute path of the site or a url of THIS origin is followed (see safe-redirect.ts): `?callbackUrl=javascript:...`
    // used to run script here, `//evil.example` to take the user away. No "platform domain" rule here: NEXT_PUBLIC_BASE_DOMAIN
    // is fixed into the bundle when it is built, the production image is built without it and `baseDomain` above falls
    // back to the STAGING domain, so such a rule would have let production's login go on to staging. Nothing legitimate
    // needs another host (route-guard sends a url of the same origin; tenant-redirect runs on the server and keeps a path).
    const resolveCallbackUrl = () => safeCallbackUrl(rawCallbackUrl, "/inventory", { origin: window.location.origin });

    const branding = useBranding();
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(
        errorParam === 'no_tenant' ? 'Tài khoản chưa được gán phòng khám. Liên hệ quản trị viên.' : ''
    );

    const handleCredentialsLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!username || !password) {
            setError('Vui lòng nhập username và password');
            return;
        }
        
        setLoading(true);
        setError('');
        
        try {
            const result = await signIn("credentials", {
                username,
                password,
                redirect: false,
            });
            
            if (result?.error) {
                setError('Username hoặc password không đúng');
            } else {
                localStorage.setItem('pms-last-activity', Date.now().toString());
                const callbackUrl = resolveCallbackUrl();
                if (isPlatformDomain) {
                    window.location.href = `/api/auth/tenant-redirect?callbackUrl=${encodeURIComponent(callbackUrl)}`;
                } else {
                    window.location.href = callbackUrl;
                }
            }
        } catch (err) {
            console.error('Login error:', err);
            setError('Username hoặc password không đúng');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 via-white to-purple-50">
            <div className="w-full max-w-md p-8">
                <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
                    {/* Logo/Brand */}
                    <div className="text-center mb-8">
                        {branding.logoUrl ? (
                            <img src={branding.logoUrl} alt="" className="w-16 h-16 mx-auto mb-4 rounded-2xl object-contain" />
                        ) : (
                            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-indigo-100 mb-4">
                                <svg className="w-8 h-8 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                                </svg>
                            </div>
                        )}
                        <h1 className="text-2xl font-bold text-gray-900 mb-2">{branding.name}</h1>
                        <p className="text-gray-500 text-sm">{branding.tagline}</p>
                    </div>

                    {/* Credentials Login Form */}
                    <form onSubmit={handleCredentialsLogin} className="space-y-4 mb-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Username</label>
                            <input
                                type="text"
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                                placeholder="Nhập username"
                                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                            <input
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="••••••••"
                                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                            />
                        </div>
                        
                        {error && (
                            <div className="text-red-500 text-sm text-center">{error}</div>
                        )}
                        
                        <Button variant="default"  
                            type="submit"
                            disabled={loading}
                            className="w-full py-3 bg-indigo-600 text-white font-medium rounded-xl hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {loading ? 'Đang đăng nhập...' : 'Đăng nhập'}
                        </Button>
                    </form>

                    {/* Divider */}
                    <div className="relative mb-6">
                        <div className="absolute inset-0 flex items-center">
                            <div className="w-full border-t border-gray-200"></div>
                        </div>
                        <div className="relative flex justify-center text-sm">
                            <span className="px-2 bg-white text-gray-400">hoặc</span>
                        </div>
                    </div>

                    {/* Google Sign in button */}
                    <Button variant="ghost"  
                        onClick={() => {
                            localStorage.setItem('pms-last-activity', Date.now().toString());
                            const callbackUrl = resolveCallbackUrl();
                            signIn("google", {
                                callbackUrl: isPlatformDomain
                                    ? `/api/auth/tenant-redirect?callbackUrl=${encodeURIComponent(callbackUrl)}`
                                    : callbackUrl,
                            });
                        }}
                        className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-white border-2 border-gray-200 rounded-xl text-gray-700 font-medium hover:bg-gray-50 hover:border-gray-300 transition-all duration-200 group"
                    >
                        {/* Google Icon */}
                        <svg className="w-5 h-5" viewBox="0 0 24 24">
                            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                        </svg>
                        <span>Đăng nhập bằng Google</span>
                    </Button>

                    {/* Footer */}
                    <p className="text-center text-xs text-gray-400 mt-6">
                        Authorized users only. Contact administrator for access.
                    </p>
                </div>
            </div>
        </div>
    );
}

export default function LoginPage() {
    return (
        <Suspense fallback={
            <div className="min-h-screen flex items-center justify-center">
                <div className="animate-spin w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full"></div>
            </div>
        }>
            <LoginForm />
        </Suspense>
    );
}

